import React, { createContext, useContext, useMemo, useState } from 'react';

export type Theme = 'light' | 'dark';

type ThemeStorage = Pick<Storage, 'getItem' | 'setItem'>;

type ThemeProviderProps = { children: React.ReactNode };

type ThemeProviderState = {
  theme: Theme;
  setTheme: (theme: Theme) => void;
};

const THEME_STORAGE_KEY = 'vite-ui-theme';

const initialState: ThemeProviderState = { theme: 'light', setTheme: () => undefined };

const ThemeProviderContext = createContext<ThemeProviderState>(initialState);

function browserStorage(): ThemeStorage | null {
  try {
    return window.localStorage;
  } catch {
    return null;
  }
}

export function readThemePreference(storage: ThemeStorage | null = browserStorage()): Theme {
  try {
    const storedTheme = storage?.getItem(THEME_STORAGE_KEY);
    return storedTheme === 'dark' || storedTheme === 'light' ? storedTheme : 'light';
  } catch {
    return 'light';
  }
}

export function persistThemePreference(theme: Theme, storage: ThemeStorage | null = browserStorage()): void {
  try {
    storage?.setItem(THEME_STORAGE_KEY, theme);
  } catch {
    return;
  }
}

export function applyProtectedTheme(theme: Theme, isProtectedAppPage: boolean, root = document.documentElement): void {
  root.classList.remove('light', 'dark');
  root.classList.add(isProtectedAppPage ? theme : 'light');
}

export function ThemeProvider({ children }: ThemeProviderProps) {
  const [theme, setThemeState] = useState<Theme>(() => readThemePreference());

  const value = useMemo(() => ({
    theme,
    setTheme: (nextTheme: Theme) => {
      setThemeState(nextTheme);
      persistThemePreference(nextTheme);
    },
  }), [theme]);

  return <ThemeProviderContext.Provider value={value}>{children}</ThemeProviderContext.Provider>;
}

export const useTheme = () => useContext(ThemeProviderContext);
